import React from 'react';
import { Link } from 'react-router-dom';
import {
  User, Settings, Sparkles, Heart, CheckCircle, Loader2,
  ChevronRight, Edit2
} from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Input } from '../ui/input';

const profTypeLabel = (type) => {
  if (type === 'therapist') return 'Terapeuta';
  if (type === 'coach') return 'Mentor';
  return 'Espiritualista';
};

export function ProfileTab({
  user, editing, saving, profileData, isProfessional,
  allSpecializations, onSetEditing, onSave,
  onSetProfileData, onToggleInterest, onToggleProfType
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 space-y-8">
        {/* Profile Card */}
        <div className="card-theraflow p-8" data-testid="profile-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-medium text-[#292927]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <User className="w-5 h-5 inline-block mr-2" strokeWidth={1.5} />
              Meu Perfil
            </h2>
            <button
              onClick={() => editing ? onSave() : onSetEditing(true)}
              disabled={saving}
              className="btn-secondary text-sm py-2 flex items-center gap-2"
              data-testid="edit-profile-button"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : editing ? (
                <><CheckCircle className="w-4 h-4" strokeWidth={1.5} /> Salvar</>
              ) : (
                <><Edit2 className="w-4 h-4" strokeWidth={1.5} /> Editar</>
              )}
            </button>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-[#292927]">Nome</Label>
                {editing ? (
                  <Input
                    value={profileData.name}
                    onChange={(e) => onSetProfileData({ ...profileData, name: e.target.value })}
                    className="input-theraflow mt-1"
                    data-testid="profile-name-input"
                  />
                ) : (
                  <p className="text-[#63635E] mt-1">{user?.name || '-'}</p>
                )}
              </div>
              <div>
                <Label className="text-sm font-medium text-[#292927]">Telefone</Label>
                {editing ? (
                  <Input
                    value={profileData.phone}
                    onChange={(e) => onSetProfileData({ ...profileData, phone: e.target.value })}
                    className="input-theraflow mt-1"
                    placeholder="(11) 99999-9999"
                    data-testid="profile-phone-input"
                  />
                ) : (
                  <p className="text-[#63635E] mt-1">{user?.phone || '-'}</p>
                )}
              </div>
            </div>
            <div className="border-t border-[#EAE8E3] pt-6">
              <Label className="text-sm font-medium text-[#292927]">Email</Label>
              <p className="text-[#63635E] mt-1">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Preferences */}
        {!isProfessional && (
          <div className="card-theraflow p-8" data-testid="preferences-section">
            <h2 className="text-xl font-medium text-[#292927] mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <Settings className="w-5 h-5 inline-block mr-2" strokeWidth={1.5} />
              Preferências de Atendimento
            </h2>

            {editing ? (
              <div className="space-y-6">
                <div>
                  <Label className="text-sm font-medium text-[#292927] mb-3 block">Tipo de Profissional Preferido</Label>
                  <div className="flex flex-wrap gap-3">
                    {['therapist', 'coach', 'spiritualist'].map((type) => (
                      <div key={type} className="flex items-center space-x-2">
                        <Checkbox
                          id={`pref-${type}`}
                          checked={(profileData.profile?.preferred_professional_type || []).includes(type)}
                          onCheckedChange={() => onToggleProfType(type)}
                        />
                        <Label htmlFor={`pref-${type}`} className="text-sm cursor-pointer">{profTypeLabel(type)}</Label>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#292927] mb-3 block">Áreas de Interesse</Label>
                  <div className="max-h-40 overflow-y-auto">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {[...new Set(allSpecializations)].slice(0, 15).map((spec) => (
                        <div key={spec} className="flex items-center space-x-2">
                          <Checkbox id={`int-${spec}`} checked={(profileData.profile?.interests || []).includes(spec)} onCheckedChange={() => onToggleInterest(spec)} />
                          <Label htmlFor={`int-${spec}`} className="text-xs cursor-pointer truncate">{spec}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="experience"
                    checked={profileData.profile?.previous_therapy_experience || false}
                    onCheckedChange={(checked) => onSetProfileData(prev => ({
                      ...prev, profile: { ...prev.profile, previous_therapy_experience: checked }
                    }))}
                  />
                  <Label htmlFor="experience" className="text-sm cursor-pointer">Já fiz terapia/mentoria anteriormente</Label>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {(profileData.profile?.preferred_professional_type || []).length > 0 && (
                  <div>
                    <p className="text-sm text-[#A0A09A] mb-2">Tipo preferido:</p>
                    <div className="flex flex-wrap gap-2">
                      {(profileData.profile?.preferred_professional_type || []).map((type) => (
                        <span key={type} className="px-3 py-1 bg-[#F6EBE6] text-[#292927] text-sm rounded-full">{profTypeLabel(type)}</span>
                      ))}
                    </div>
                  </div>
                )}
                {(profileData.profile?.interests || []).length > 0 && (
                  <div>
                    <p className="text-sm text-[#A0A09A] mb-2">Interesses:</p>
                    <div className="flex flex-wrap gap-2">
                      {(profileData.profile?.interests || []).slice(0, 5).map((item) => (
                        <span key={item} className="px-3 py-1 bg-[#4A5D4E]/10 text-[#4A5D4E] text-sm rounded-full">{item}</span>
                      ))}
                      {(profileData.profile?.interests || []).length > 5 && (
                        <span className="text-sm text-[#A0A09A]">+{(profileData.profile?.interests || []).length - 5}</span>
                      )}
                    </div>
                  </div>
                )}
                {!(profileData.profile?.preferred_professional_type || []).length && !(profileData.profile?.interests || []).length && (
                  <p className="text-[#A0A09A] text-sm">Nenhuma preferência definida. Clique em editar para configurar.</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Profile Sidebar */}
      <div className="space-y-6">
        <div className="card-theraflow p-6" data-testid="quick-actions-profile">
          <h3 className="text-lg font-medium text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Ações Rápidas</h3>
          <div className="space-y-3">
            <Link to="/match" className="flex items-center justify-between p-4 rounded-xl bg-[#F6EBE6] hover:bg-[#EBDCD5] transition-colors">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-[#4A5D4E]" strokeWidth={1.5} />
                <span className="font-medium text-[#292927]">Encontrar Match</span>
              </div>
              <ChevronRight className="w-5 h-5 text-[#A0A09A]" strokeWidth={1.5} />
            </Link>
            <Link to="/professionals" className="flex items-center justify-between p-4 rounded-xl bg-[#F6EBE6] hover:bg-[#EBDCD5] transition-colors">
              <div className="flex items-center gap-3">
                <Heart className="w-5 h-5 text-[#4A5D4E]" strokeWidth={1.5} />
                <span className="font-medium text-[#292927]">Ver Profissionais</span>
              </div>
              <ChevronRight className="w-5 h-5 text-[#A0A09A]" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
